import { Box, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { matchPath, useLocation, useNavigate } from 'react-router'
import { useModalState } from '~/atoms/ModalState'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import Logger from '~/services/Logger'
import { ApplicationRoutes } from '~/types/RoutingTypes'

import { PENDING_PBX_SWITCH_STATE_KEY } from './pbxSwitchState'
import { PbxSummary, usePbxSwitchExecutor } from './usePbxSwitchExecutor'

const logger = new Logger('usePbxSwitcher')

const SWITCH_PBX_CONFIRMATION_KEY = 'switch-pbx'

interface UsePbxSwitcherParams {
  /**
   * Called once a switch flow has been initiated:
   * - the user picked the same PBX (no-op), OR
   * - the confirmation modal was opened.
   *
   * Containers (modal, popover) use this to dismiss themselves.
   * Note: this is NOT called when a call-in-progress modal blocks the switch,
   * since the user may want to keep the picker available after dismissing it.
   */
  onAfterSwitchInitiated?: () => void
}

interface UsePbxSwitcherResult {
  currentPbx: PbxSummary | undefined
  handleSelect: (selectedPbx: PbxSummary) => void
}

/**
 * Encapsulates all the side-effects of switching PBXs: confirmation modal,
 * call-in-progress guard, recents tracking, analytics, and the success toast
 * emitted once the new session has loaded.
 *
 * Lives as a hook (rather than a component) so that the underlying React
 * Query / `useState` state survives the close of any modal or popover that
 * contains the picker UI -- closing the surface must not unmount the hooks
 * that the confirmation `onConfirm` callback relies on.
 */
export const usePbxSwitcher = ({
  onAfterSwitchInitiated,
}: UsePbxSwitcherParams = {}): UsePbxSwitcherResult => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentPbx, executePbxSwitch } = usePbxSwitchExecutor()

  const {
    confirmModal: [, setConfirmModalState],
    callInProgressModal: [, setCallInProgressModalState],
  } = useModalState()

  const {
    state: {
      phoneCalls: { list },
    },
  } = usePhoneEndpoint({})

  const hasOpenCall = list.callList.size > 0

  const handleSelect = (selectedPbx: PbxSummary) => {
    if (currentPbx?.id === selectedPbx.id) {
      onAfterSwitchInitiated?.()
      return
    }

    logger.info(`Attempting to switch to pbx ${selectedPbx.id}`)

    if (hasOpenCall) {
      logger.info('Cannot switch PBX while a call is in progress')
      setCallInProgressModalState({ open: true })
      return
    }

    setConfirmModalState({
      title: t('profileSettings.switchPbxConfirmation.title'),
      message: (
        <Box>
          <Text display="inline" color="fg.default">
            {t('profileSettings.switchPbxConfirmation.message', {
              pbxName: currentPbx?.name,
            })}
            {'\u00A0'}
          </Text>
        </Box>
      ),
      confirmText: t('profileSettings.switchPbxConfirmation.confirmText'),
      cancelText: t('profileSettings.switchPbxConfirmation.cancelText'),
      closeAfterConfirm: true,
      isDestructive: false,
      dontShowAgainKey: SWITCH_PBX_CONFIRMATION_KEY,
      onConfirm: () => {
        const isSwitchboardEditRoute = Boolean(
          matchPath(
            { path: ApplicationRoutes.SWITCHBOARD_EDIT, end: true },
            location.pathname,
          ),
        )

        if (isSwitchboardEditRoute) {
          navigate(ApplicationRoutes.SWITCHBOARDS, {
            state: { [PENDING_PBX_SWITCH_STATE_KEY]: selectedPbx },
          })
          return
        }

        executePbxSwitch(selectedPbx)
      },
    })

    onAfterSwitchInitiated?.()
  }

  return {
    currentPbx,
    handleSelect,
  }
}
